import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Upload, X, Loader, Check } from 'lucide-react';

const GRADE_INFO = {
  A: {
    color: '#4ade80',
    label: 'Great Shape',
    message: "Your interior is holding up well. A monthly maintenance plan keeps it exactly here — no deep reset needed, just regular upkeep to protect your investment.",
    recommendation: 'maintenance',
  },
  B: {
    color: '#a3e635',
    label: 'Light Wear',
    message: "Some buildup and light staining starting to appear. A one-time interior detail will bring this back to fresh — no intensive reset needed.",
    recommendation: 'onetime',
  },
  C: {
    color: '#facc15',
    label: 'Needs Attention',
    message: "Noticeable dirt and staining present. A full interior detail with the right add-ons will bring this back to clean.",
    recommendation: 'onetime',
  },
  D: {
    color: '#fb923c',
    label: 'Heavy Build-Up',
    message: "Significant staining, odor, or pet hair. The Alchemist Reset includes full steam and carpet shampoo — the right starting point to bring this back to pristine, then maintenance keeps it there.",
    recommendation: 'maintenance',
  },
  F: {
    color: '#f87171',
    label: 'Full Reset Needed',
    message: "Your interior needs a complete overhaul. The Alchemist Reset handles it all — steam, deep shampoo, bio odor treatment — and gets you back to factory fresh.",
    recommendation: 'maintenance',
  },
};

// Maps quiz answers → booking wizard add-on IDs
function getSuggestedAddOns(answers) {
  const addons = [];
  if (answers.petHair) addons.push({ id: 'pet_hair', label: 'Pet Hair Removal', emoji: '🐾', reason: 'visible pet hair' });
  if (answers.stains) addons.push({ id: 'heavy_stain', label: 'Heavy Stain Treatment', emoji: '🧹', reason: 'visible stains' });
  if (answers.odor) addons.push({ id: 'odor_removal', label: 'Odor Elimination', emoji: '🌬️', reason: 'noticeable odor' });
  if (answers.carSeat) addons.push({ id: 'car_seat', label: 'Car Seat Deep Clean', emoji: '👶', reason: 'child car seat' });
  return addons;
}

export default function InteriorQuizPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(0); // 0=questions 1=photos 2=contact 3=results
  const [answers, setAnswers] = useState({ petHair: null, carSeat: null, stains: null, odor: null, condition: '' });
  const [photos, setPhotos] = useState([]);
  const [contact, setContact] = useState({ name: '', email: '', phone: '' });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const questionsComplete = answers.petHair !== null && answers.carSeat !== null && answers.stains !== null && answers.odor !== null && answers.condition !== '';
  const contactComplete = contact.name && contact.email && contact.phone;

  const handlePhotoAdd = (e) => {
    const files = Array.from(e.target.files);
    const newPhotos = files.slice(0, 3 - photos.length).map(file => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setPhotos(prev => [...prev, ...newPhotos].slice(0, 3));
  };

  const removePhoto = (i) => {
    setPhotos(prev => prev.filter((_, idx) => idx !== i));
  };

  // Phone camera photos run 3-8MB+ raw — sent as-is (base64, up to 3 of them)
  // that blows past Vercel's ~4.5MB serverless request body limit and the
  // request gets rejected with a 413 before /api/ai ever runs. Downscale and
  // re-compress to a JPEG well under that ceiling before sending.
  const toBase64 = (file, maxDim = 1600, quality = 0.75) => new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      let { width, height } = img;
      if (width > height && width > maxDim) {
        height = Math.round(height * (maxDim / width));
        width = maxDim;
      } else if (height > maxDim) {
        width = Math.round(width * (maxDim / height));
        height = maxDim;
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality).split(',')[1]);
    };
    img.onerror = reject;
    img.src = objectUrl;
  });

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const photoData = await Promise.all(photos.map(p => toBase64(p.file)));
      const res = await fetch('/api/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'analyze', photos: photoData, answers, contact }),
      });
      if (!res.ok) throw new Error('Analysis failed');
      const data = await res.json();
      setResult(data);
      setStep(3);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBookNow = () => {
    const grade = result ? GRADE_INFO[result.score] || GRADE_INFO['C'] : null;
    const suggestedAddOns = getSuggestedAddOns(answers);
    const addonsParam = suggestedAddOns.map(a => a.id).join(',');

    // Save contact info for booking wizard to pre-fill
    try {
      sessionStorage.setItem('quiz_prefill', JSON.stringify({
        name: contact.name,
        email: contact.email,
        phone: contact.phone.replace(/\D/g, ''),
      }));
    } catch {}

    if (grade?.recommendation === 'maintenance') {
      navigate('/maintenance');
    } else {
      const url = `/detail/book?package=interior${addonsParam ? `&addons=${addonsParam}` : ''}`;
      navigate(url);
    }
  };

  const grade = result ? GRADE_INFO[result.score] || GRADE_INFO['C'] : null;
  const suggestedAddOns = result ? getSuggestedAddOns(answers) : [];

  return (
    <div className="min-h-screen bg-cloud text-[#111827]">

      {/* Header */}
      <div className="px-6 pt-6 pb-4 max-w-xl mx-auto flex items-center gap-4">
        <button onClick={() => step === 0 ? navigate('/') : setStep(s => s - 1)} className="text-[#6B7280] hover:text-[#111827] transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        {step < 3 && (
          <div className="flex-1 bg-gray-200 rounded-full h-1.5">
            <div
              className="bg-[#68caf1] h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${((step + 1) / 3) * 100}%` }}
            />
          </div>
        )}
      </div>

      <div className="px-6 pb-24 max-w-xl mx-auto">

        {/* Step 0 — Questions */}
        {step === 0 && (
          <div>
            <p className="text-[#68caf1] text-xs font-bold uppercase tracking-widest mb-2">Free Interior Score</p>
            <h1 className="text-3xl font-bold mb-2">How's your interior?</h1>
            <p className="text-[#6B7280] text-sm mb-8">5 direct questions — we'll tell you exactly what your car needs and what it'll cost.</p>

            {[
              { key: 'petHair', label: 'Is there visible pet hair on your seats or carpet?' },
              { key: 'stains', label: 'Are there any visible stains on your carpet or seats?' },
              { key: 'odor', label: 'Is there any odor inside your car you\'d like eliminated?' },
              { key: 'carSeat', label: 'Do you have a child car seat that needs sanitizing and deep cleaning?' },
            ].map(({ key, label }) => (
              <div key={key} className="mb-5">
                <p className="font-bold mb-3 text-sm">{label}</p>
                <div className="flex gap-3">
                  {['Yes', 'No'].map(opt => (
                    <button
                      key={opt}
                      onClick={() => setAnswers(a => ({ ...a, [key]: opt === 'Yes' }))}
                      className={`flex-1 py-3 rounded-xl border-2 text-sm font-bold transition-all ${answers[key] === (opt === 'Yes') ? 'border-[#68caf1] bg-[#68caf1]/8 text-[#111827]' : 'border-gray-200 bg-white text-[#6B7280] hover:border-[#68caf1]/40'}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            <div className="mb-5">
              <p className="font-bold mb-3 text-sm">How would you describe your interior overall?</p>
              <div className="space-y-2">
                {[
                  { val: 'fresh', label: 'Pretty clean — just need a refresh' },
                  { val: 'light', label: 'Some buildup and light wear' },
                  { val: 'dirty', label: 'Noticeably dirty — been a while' },
                  { val: 'heavy', label: 'Heavily soiled or neglected' },
                ].map(({ val, label }) => (
                  <button
                    key={val}
                    onClick={() => setAnswers(a => ({ ...a, condition: val }))}
                    className={`w-full p-3 rounded-xl border-2 text-sm font-medium text-left transition-all ${answers.condition === val ? 'border-[#68caf1] bg-[#68caf1]/8 text-[#111827]' : 'border-gray-200 bg-white text-[#374151] hover:border-[#68caf1]/40'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={() => setStep(1)}
              disabled={!questionsComplete}
              className={`w-full py-4 rounded-2xl font-bold text-sm mt-4 flex items-center justify-center gap-2 transition-all ${questionsComplete ? 'bg-[#68caf1] text-black hover:bg-[#5bb8db]' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
            >
              Next — Add Photos <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Step 1 — Photos */}
        {step === 1 && (
          <div>
            <p className="text-[#68caf1] text-xs font-bold uppercase tracking-widest mb-2">Free Interior Score</p>
            <h1 className="text-3xl font-bold mb-2">Add photos</h1>
            <p className="text-[#6B7280] text-sm mb-6">Upload at least 1 photo of your interior — seats, floor, or dashboard. The more we see, the more accurate your score.</p>

            <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoAdd} />

            {photos.length < 3 && (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full border-2 border-dashed border-gray-300 rounded-2xl py-10 flex flex-col items-center gap-3 hover:border-[#68caf1] transition-colors bg-white mb-4"
              >
                <Upload className="w-8 h-8 text-[#9CA3AF]" />
                <p className="text-sm font-medium text-[#6B7280]">Tap to upload photos</p>
                <p className="text-xs text-[#9CA3AF]">{photos.length}/3 added</p>
              </button>
            )}

            {photos.length > 0 && (
              <div className="grid grid-cols-3 gap-3 mb-6">
                {photos.map((p, i) => (
                  <div key={i} className="relative rounded-xl overflow-hidden aspect-square">
                    <img src={p.preview} alt="" className="w-full h-full object-cover" />
                    <button
                      onClick={() => removePhoto(i)}
                      className="absolute top-1 right-1 w-6 h-6 bg-black/60 rounded-full flex items-center justify-center"
                    >
                      <X className="w-3 h-3 text-white" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <p className="text-xs text-[#9CA3AF] mb-6 text-center">Photos help Akeem give you a more accurate score — they're not stored or shared.</p>

            <button
              onClick={() => setStep(2)}
              disabled={photos.length === 0}
              className={`w-full py-4 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 transition-all ${photos.length > 0 ? 'bg-[#68caf1] text-black hover:bg-[#5bb8db]' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
            >
              Next — Get My Score <ArrowRight className="w-4 h-4" />
            </button>
            {photos.length === 0 && (
              <p className="text-xs text-center text-[#9CA3AF] mt-2">At least 1 photo is required to continue</p>
            )}
          </div>
        )}

        {/* Step 2 — Contact */}
        {step === 2 && (
          <div>
            <p className="text-[#68caf1] text-xs font-bold uppercase tracking-widest mb-2">Almost There</p>
            <h1 className="text-3xl font-bold mb-2">Where should we send your results?</h1>
            <p className="text-[#6B7280] text-sm mb-8">Enter your info and we'll show your score instantly.</p>

            {[
              { key: 'name', label: 'Your Name', placeholder: 'First name', type: 'text' },
              { key: 'email', label: 'Email', placeholder: 'your@email.com', type: 'email' },
              { key: 'phone', label: 'Phone', placeholder: '(301) 555-0000', type: 'tel' },
            ].map(({ key, label, placeholder, type }) => (
              <div key={key} className="mb-4">
                <label className="block text-sm font-bold mb-1.5">{label}</label>
                <input
                  type={type}
                  value={contact[key]}
                  onChange={e => setContact(c => ({ ...c, [key]: e.target.value }))}
                  placeholder={placeholder}
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm focus:border-[#68caf1] focus:outline-none"
                />
              </div>
            ))}

            {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

            <button
              onClick={handleSubmit}
              disabled={!contactComplete || loading}
              className={`w-full py-4 rounded-2xl font-bold text-sm mt-2 flex items-center justify-center gap-2 transition-all ${contactComplete && !loading ? 'bg-[#68caf1] text-black hover:bg-[#5bb8db]' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
            >
              {loading ? <><Loader className="w-4 h-4 animate-spin" /> Analyzing your interior…</> : 'Get My Interior Score'}
            </button>

            <p className="text-xs text-[#9CA3AF] text-center mt-3">No spam. We'll only reach out about your car.</p>
          </div>
        )}

        {/* Step 3 — Results */}
        {step === 3 && result && grade && (
          <div>
            <p className="text-[#68caf1] text-xs font-bold uppercase tracking-widest mb-6">Your Interior Score</p>

            {/* Grade card */}
            <div className="bg-white border-2 rounded-2xl p-8 text-center mb-5" style={{ borderColor: grade.color }}>
              <p className="text-7xl font-black mb-2" style={{ color: grade.color }}>{result.score}</p>
              <p className="font-bold text-xl mb-1" style={{ color: grade.color }}>{grade.label}</p>
              <p className="text-[#6B7280] text-sm leading-relaxed mt-3">{result.summary || grade.message}</p>
            </div>

            {/* Suggested add-ons based on their answers */}
            {suggestedAddOns.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-2xl p-5 mb-5">
                <p className="text-xs font-bold text-[#68caf1] uppercase tracking-widest mb-3">Recommended Add-Ons</p>
                <p className="text-xs text-[#6B7280] mb-4">Based on what you told us — these will be pre-selected when you book.</p>
                <div className="space-y-2">
                  {suggestedAddOns.map(addon => (
                    <div key={addon.id} className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full bg-[#68caf1] flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-black" />
                      </div>
                      <span className="text-sm font-medium text-[#111827]">{addon.emoji} {addon.label}</span>
                      <span className="text-xs text-[#9CA3AF] ml-auto">{addon.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recommendation */}
            <div className="bg-white border-2 border-[#68caf1]/40 rounded-2xl p-6 mb-5">
              <p className="text-[#68caf1] text-xs font-bold uppercase tracking-widest mb-2">Recommended</p>
              <p className="font-bold text-lg mb-1 text-[#111827]">
                {grade.recommendation === 'maintenance'
                  ? 'Alchemist Reset + Monthly Maintenance'
                  : 'Advanced Interior Detail'}
              </p>
              <p className="text-gray-500 text-sm leading-relaxed">
                {grade.recommendation === 'maintenance'
                  ? 'Start with the Alchemist Reset from $200 to get your car to pristine — steam, shampoo, full deep clean — then from $75/month keeps it there.'
                  : 'Our Advanced interior package — your info is already filled in, just pick a time. Want more or less? Switch packages on the next screen.'}
              </p>
            </div>

            {/* Primary CTA — pre-filled booking */}
            <button
              onClick={handleBookNow}
              className="w-full bg-[#68caf1] text-black font-bold py-4 rounded-2xl mb-3 hover:bg-[#5bb8db] transition-all"
            >
              {grade.recommendation === 'maintenance' ? 'See the Maintenance Program →' : 'Book Now — Info Pre-Filled →'}
            </button>

            <a
              href="https://monarchdetailing.art/monarch.vcf"
              className="w-full flex items-center justify-center gap-2 bg-white border border-gray-200 text-[#111827] font-semibold py-3.5 rounded-2xl hover:border-[#68caf1] transition-all text-sm mb-3"
            >
              📱 Save Akeem's Contact
            </a>

            <button
              onClick={() => navigate('/')}
              className="w-full bg-white border border-gray-200 text-[#6B7280] font-medium py-3 rounded-2xl hover:border-gray-300 transition-all text-sm"
            >
              Back to Home
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
